import React from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { colors } from '../../styles/theme/colors';

const expandActionEasing = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** Fine pointer: icon-only idle, expand label on hover/focus. Coarse/touch: label always visible. */
const finePointerQuery = '@media (hover: hover) and (pointer: fine)';

export interface HoverExpandActionProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  disabledTitle?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  'aria-label'?: string;
}

const HoverExpandAction: React.FC<HoverExpandActionProps> = ({
  label,
  icon,
  onClick,
  disabled = false,
  disabledTitle,
  variant = 'secondary',
  'aria-label': ariaLabel,
}) => {
  const button = (
    <Button
      size="small"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      sx={(theme) => {
        const isPrimary = variant === 'primary';
        const isGhost = variant === 'ghost';
        const accent = colors.success.main;

        const expandedSurface = isGhost
          ? {
              color: theme.palette.text.primary,
              bgcolor: alpha(colors.grey[500], 0.08),
            }
          : isPrimary
            ? {
                background: 'linear-gradient(to right, #0891b2, #059669)',
              }
            : {
                backgroundColor: alpha(accent, 0.12),
              };

        const expandedLabel = {
          maxWidth: 140,
          opacity: 1,
          marginLeft: 0.75,
        };

        return {
          minWidth: 36,
          height: 36,
          px: 1.25,
          py: 0,
          maxWidth: 200,
          borderRadius: 2,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          lineHeight: 1,
          boxShadow: 'none',
          justifyContent: 'flex-start',
          border: '1px solid',
          borderColor: disabled
            ? theme.palette.divider
            : isPrimary
              ? 'transparent'
              : isGhost
                ? 'transparent'
                : alpha(accent, 0.35),
          color: disabled
            ? theme.palette.text.disabled
            : isPrimary
              ? '#fff'
              : isGhost
                ? theme.palette.text.secondary
                : accent,
          background: disabled
            ? 'transparent'
            : isPrimary
              ? `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`
              : isGhost
                ? 'transparent'
                : alpha(accent, 0.06),
          transition: [
            `max-width 360ms ${expandActionEasing}`,
            `padding 360ms ${expandActionEasing}`,
            `background 240ms ease`,
            `border-color 240ms ease`,
            `box-shadow 240ms ease`,
            `transform 240ms ${expandActionEasing}`,
          ].join(', '),
          '& .expandLabel': {
            display: 'inline-block',
            maxWidth: 140,
            opacity: 1,
            overflow: 'hidden',
            marginLeft: 0.75,
            transition: [
              `max-width 360ms ${expandActionEasing}`,
              `opacity 220ms ease`,
              `margin-left 360ms ${expandActionEasing}`,
            ].join(', '),
          },
          // Collapse to icon-only only when a real hover device is available.
          [finePointerQuery]: {
            maxWidth: 36,
            px: 1,
            '& .expandLabel': {
              maxWidth: 0,
              opacity: 0,
              marginLeft: 0,
            },
            '&:hover:not(.Mui-disabled), &:focus-visible:not(.Mui-disabled)': {
              maxWidth: 200,
              px: 1.25,
              boxShadow: isPrimary
                ? `0 4px 14px ${alpha(accent, 0.28)}`
                : `0 4px 12px ${alpha(accent, 0.12)}`,
              transform: 'translateY(-1px)',
              ...expandedSurface,
              '& .expandLabel': expandedLabel,
            },
          },
          '&:focus-visible:not(.Mui-disabled)': {
            maxWidth: 200,
            px: 1.25,
            boxShadow: isPrimary
              ? `0 4px 14px ${alpha(accent, 0.28)}`
              : `0 4px 12px ${alpha(accent, 0.12)}`,
            ...expandedSurface,
            '& .expandLabel': expandedLabel,
          },
          '&.Mui-disabled': {
            borderColor: theme.palette.divider,
          },
        };
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 20,
          height: 20,
          '& > svg': { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box component="span" className="expandLabel">
        {label}
      </Box>
    </Button>
  );

  if (disabled && disabledTitle != null && disabledTitle !== '') {
    return (
      <Tooltip title={disabledTitle}>
        <span>{button}</span>
      </Tooltip>
    );
  }
  return button;
};

export default HoverExpandAction;
